import { useEffect, useRef, useState } from 'react';

const dataProp = 'data-select-index';

/**
 *
 * @type {import('.').UseSelectListenerType}
 */
const useSelectListener = ({ enabled, ref, selector }) => {
  const state = useState(-1);
  const [hightlighted, setHightlighted] = state;
  const [nodes, setNodes] = useState([]);
  const usingKeyboard = useRef(false);

  useEffect(() => {
    const { current } = ref;
    if (!enabled || !current) {
      return undefined;
    }

    const updateList = () => {
      const items = selector ? current.querySelectorAll(selector) : current.childNodes;
      items.forEach((child, i) => {
        child.setAttribute(dataProp, i);
      });
      setNodes(items);
      setHightlighted(-1);
    };

    updateList();

    const observer = new MutationObserver(updateList);
    observer.observe(current, { subtree: false, childList: true, attributes: false });

    return () => {
      observer.disconnect();
      (selector ? current.querySelectorAll(selector) : current.childNodes).forEach((child) => {
        child.removeAttribute(dataProp);
      });
    };
  }, [ref, selector, enabled, setHightlighted]);

  useEffect(() => {
    const { length } = nodes;
    if (!enabled || !ref.current || length === 0) {
      return undefined;
    }
    const blockScrollCallback = () => {
      usingKeyboard.current = false;
    };

    const onMouseMove = (evt) => {
      if (usingKeyboard.current) {
        return;
      }
      const index = parseInt(evt.target.getAttribute(dataProp), 10);
      if (Number.isInteger(index) && index !== hightlighted) {
        setHightlighted(index);
      }
    };

    const onKeyDown = (evt) => {
      if (!['ArrowUp', 'ArrowDown'].includes(evt.code)) {
        return;
      }
      usingKeyboard.current = true;
      evt.stopPropagation();
      evt.preventDefault();
      let newIndex = evt.code === 'ArrowDown' ? hightlighted + 1 : hightlighted - 1;
      if (newIndex >= length) {
        newIndex = 0;
      }
      if (newIndex < 0) {
        newIndex = length - 1;
      }
      setHightlighted(newIndex);
    };

    const { current } = ref;

    window.addEventListener('wheel', blockScrollCallback);
    window.addEventListener('mousemove', blockScrollCallback);
    window.document.body.addEventListener('keydown', onKeyDown);
    current.addEventListener('mousemove', onMouseMove);

    return () => {
      window.removeEventListener('wheel', blockScrollCallback);
      window.removeEventListener('mousemove', blockScrollCallback);
      window.document.body.removeEventListener('keydown', onKeyDown);
      current.removeEventListener('mousemove', onMouseMove);
    };
  }, [enabled, ref, state, hightlighted, setHightlighted, nodes]);

  useEffect(() => {
    if (!enabled) {
      setHightlighted(-1);
    }
  }, [enabled, setHightlighted]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const elem = ref.current.querySelector(`[${dataProp}="${hightlighted}"]`);

    if (!elem) {
      return;
    }

    if (elem.offsetTop < ref.current.scrollTop) {
      ref.current.scrollTop = elem.offsetTop;
    }
    if (elem.offsetTop + elem.clientHeight / 2 > ref.current.scrollTop + ref.current.clientHeight) {
      ref.current.scrollTop = elem.offsetTop - ref.current.clientHeight + elem.clientHeight;
    }
  }, [hightlighted, ref]);
  return state;
};

export default useSelectListener;
